import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUploader from '../components/ImageUploader';
import TermsModalContent from '../components/TermsModalContent';
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from '../components/ui/dialog';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  Image as ImageIcon,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Stethoscope,
  Info
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { createClinic } from '../services/api';
import { toast } from '../hooks/use-toast';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component for handling map clicks
const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition({
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
    },
  });

  return position ? <Marker position={[position.lat, position.lng]} /> : null;
};

const RegisterClinic = () => {
  const navigate = useNavigate();
  const { specialties, countries, cities, loading: dataLoading } = useData();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    // Información básica
    name: '',
    country: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    
    // Ubicación
    address: '',
    coordinates: null, // { lat, lng }
    
    // Especialidades y descripción
    selectedSpecialties: [],
    description: '',
    aboutLong: '',
    
    // Horarios
    schedule: {
      'Lunes - Viernes': '',
      'Sábado': '',
      'Domingo': ''
    },
    
    // Imágenes (array de 5 slots)
    images: ['', '', '', '', '']
    ,
    // Consentimientos
    termsAccepted: false,
    marketingConsent: false,
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleScheduleChange = (day, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: value
      }
    }));
  };

  const handleSpecialtyToggle = (specialtyId) => {
    setFormData(prev => ({
      ...prev,
      selectedSpecialties: prev.selectedSpecialties.includes(specialtyId)
        ? prev.selectedSpecialties.filter(id => id !== specialtyId)
        : [...prev.selectedSpecialties, specialtyId]
    }));
  };

  const handleImagesChange = (newImages) => {
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.country || !formData.city || !formData.phone || !formData.email) {
          toast({
            title: "Campos requeridos",
            description: "Por favor completa todos los campos obligatorios",
            variant: "destructive"
          });
          return false;
        }
        return true;
      case 2:
        if (!formData.address) {
          toast({
            title: "Dirección requerida",
            description: "Por favor ingresa la dirección de tu clínica",
            variant: "destructive"
          });
          return false;
        }
        return true;
      case 3:
        if (formData.selectedSpecialties.length === 0) {
          toast({
            title: "Especialidad requerida",
            description: "Selecciona al menos una especialidad",
            variant: "destructive"
          });
          return false;
        }
        if (!formData.description || formData.description.trim() === '') {
          toast({
            title: "Descripción requerida",
            description: "Por favor agrega una descripción de tu clínica",
            variant: "destructive"
          });
          return false;
        }
        return true;
      case 4:
        // Paso 4: validar aceptación de términos (requerido)
        if (!formData.termsAccepted) {
          toast({
            title: "Aceptar términos",
            description: "Debes aceptar los términos de uso de información para registrar la clínica.",
            variant: "destructive"
          });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Validando paso:', currentStep);
    console.log('Datos del formulario:', formData);
    if (validateStep(currentStep)) {
      console.log('Validación exitosa, avanzando al paso:', currentStep + 1);
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      console.log('Validación falló');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      setSubmitting(true);
      try {
        // Get uploaded images (filter out empty slots)
        const uploadedImages = formData.images.filter(img => img.trim() !== '');
        const mainImage = uploadedImages[0] || '';
        const galleryImages = uploadedImages.slice(1);

        // Prepare data for API
        const clinicData = {
          name: formData.name,
          country: formData.country,
          city: formData.city,
          phone: formData.phone,
          email: formData.email || null,
          website: formData.website || null,
          address: formData.address,
          coordinates: formData.coordinates || { lat: -16.5, lng: -68.15 },
          specialties: formData.selectedSpecialties,
          description: formData.description,
          aboutLong: formData.aboutLong || null,
          schedule: Object.entries(formData.schedule)
            .filter(([_, hours]) => hours.trim() !== '')
            .map(([day, hours]) => ({ day, hours })),
          mainImage: mainImage,
          galleryImages: galleryImages
        };

        // Agregar consentimientos a los datos enviados
        clinicData.terms_accepted = formData.termsAccepted;
        clinicData.marketing_consent = formData.marketingConsent;

        console.log('Enviando datos:', clinicData);

        // Send to API
        const response = await createClinic(clinicData);
        
        console.log('Respuesta:', response);

        toast({
          title: "¡Registro exitoso!",
          description: "Tu clínica ha sido registrada. Te contactaremos pronto para verificar la información.",
        });

        // Wait a bit before redirecting
        setTimeout(() => {
          navigate('/clinicas');
        }, 2000);
      } catch (error) {
        console.error('Error al registrar clínica:', error);
        toast({
          title: "Error al registrar",
          description: error.response?.data?.detail || "Ocurrió un error al registrar la clínica. Por favor intenta de nuevo.",
          variant: "destructive"
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const availableCities = formData.country && cities ? (cities[formData.country] || []) : [];

  // Show loading state while data is being fetched
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-white py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="Tu ClinicApp Logo" 
                className="h-16 w-16 object-contain rounded-md shadow-lg"
              />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Registra tu Clínica Dental</h1>
          <p className="text-gray-600">Únete a nuestro directorio y aumenta tu visibilidad</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                      step < currentStep
                        ? 'bg-green-500 text-white'
                        : step === currentStep
                        ? 'bg-cyan-500 text-white shadow-lg scale-110'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step < currentStep ? (
                      <CheckCircle2 className="h-6 w-6" />
                    ) : (
                      step
                    )}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    step === currentStep ? 'text-cyan-600' : 'text-gray-500'
                  }`}>
                    {step === 1 && 'Información'}
                    {step === 2 && 'Ubicación'}
                    {step === 3 && 'Servicios'}
                    {step === 4 && 'Horarios'}
                  </span>
                </div>
                {step < 4 && (
                  <div
                    className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                      step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card className="shadow-xl">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && currentStep < totalSteps) e.preventDefault(); }}>
              {/* Step 1: Información Básica */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl flex items-center">
                      <Building2 className="h-6 w-6 mr-2 text-cyan-500" />
                      Información Básica
                    </CardTitle>
                  </CardHeader>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="name">Nombre de la Clínica *</Label>
                      <Input
                        id="name"
                        placeholder="Ej: Clínica Dental Sonrisa"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="country">País *</Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) => {
                          handleInputChange('country', value);
                          handleInputChange('city', '');
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un país" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.flag} {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="city">Ciudad *</Label>
                      <Select
                        value={formData.city}
                        onValueChange={(value) => handleInputChange('city', value)}
                        disabled={!formData.country}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una ciudad" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCities.map(city => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="phone">Teléfono *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+591 22441234"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="contacto@clinica.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="website">Sitio Web (opcional)</Label>
                      <Input
                        id="website"
                        placeholder="www.clinica.com"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Ubicación */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl flex items-center">
                      <MapPin className="h-6 w-6 mr-2 text-cyan-500" />
                      Ubicación
                    </CardTitle>
                  </CardHeader>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address">Dirección Completa *</Label>
                      <Textarea
                        id="address"
                        placeholder="Ej: Av. 16 de Julio 1234, Edificio Medical Center, Piso 3"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={3}
                        required
                      />
                    </div>

                    <div>
                      <Label>Ubicación en el Mapa</Label>
                      <p className="text-sm text-gray-500 mb-3 flex items-start">
                        <Info className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                        Haz clic en el mapa para marcar la ubicación exacta de tu clínica
                      </p>
                      <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md" style={{ height: '400px' }}>
                        <MapContainer
                          center={formData.coordinates || [-16.5, -68.15]}
                          zoom={13}
                          style={{ height: '100%', width: '100%' }}
                          key={`map-${formData.coordinates?.lat}-${formData.coordinates?.lng}`}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <LocationMarker
                            position={formData.coordinates}
                            setPosition={(coords) => handleInputChange('coordinates', coords)}
                          />
                        </MapContainer>
                      </div>
                      {formData.coordinates && (
                        <div className="mt-2 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                          <p className="text-sm text-gray-700">
                            <strong>Coordenadas seleccionadas:</strong>
                            <br />
                            Latitud: {formData.coordinates.lat.toFixed(6)}
                            <br />
                            Longitud: {formData.coordinates.lng.toFixed(6)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Servicios y Descripción */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl flex items-center">
                      <Stethoscope className="h-6 w-6 mr-2 text-cyan-500" />
                      Servicios y Descripción
                    </CardTitle>
                  </CardHeader>

                  <div className="space-y-4">
                    <div>
                      <Label>Especialidades Ofrecidas *</Label>
                      <p className="text-sm text-gray-500 mb-3">Selecciona todas las que apliquen</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {specialties.map((specialty) => (
                          <div
                            key={specialty.id}
                            onClick={() => handleSpecialtyToggle(specialty.id)}
                            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.selectedSpecialties.includes(specialty.id)
                                ? 'border-cyan-500 bg-cyan-50'
                                : 'border-gray-200 hover:border-cyan-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-sm">{specialty.name}</h4>
                                <p className="text-xs text-gray-500">{specialty.description}</p>
                              </div>
                              {formData.selectedSpecialties.includes(specialty.id) && (
                                <CheckCircle2 className="h-5 w-5 text-cyan-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="description">Descripción Corta *</Label>
                      <Textarea
                        id="description"
                        placeholder="Una breve descripción de tu clínica (máx. 150 caracteres)"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={2}
                        maxLength={150}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.description.length}/150 caracteres
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="aboutLong">Descripción Detallada (opcional)</Label>
                      <Textarea
                        id="aboutLong"
                        placeholder="Describe tu clínica, servicios, experiencia y qué te hace único..."
                        value={formData.aboutLong}
                        onChange={(e) => handleInputChange('aboutLong', e.target.value)}
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Horarios e Imágenes */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl flex items-center">
                      <Clock className="h-6 w-6 mr-2 text-cyan-500" />
                      Horarios e Imágenes
                    </CardTitle>
                  </CardHeader>

                  <div className="space-y-6">
                    <div>
                      <Label>Horarios de Atención</Label>
                      <div className="space-y-3 mt-3">
                        {Object.keys(formData.schedule).map((day) => (
                          <div key={day} className="grid grid-cols-2 gap-4 items-center">
                            <Label className="text-sm font-medium">{day}</Label>
                            <Input
                              placeholder="Ej: 8:00 AM - 6:00 PM"
                              value={formData.schedule[day]}
                              onChange={(e) => handleScheduleChange(day, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <Label className="text-lg font-semibold flex items-center mb-4">
                        <ImageIcon className="h-5 w-5 mr-2 text-cyan-500" />
                        Imágenes de la Clínica
                      </Label>
                      <ImageUploader 
                        images={formData.images}
                        onImagesChange={handleImagesChange}
                      />
                      
                      <div className="mt-4">

                        <label className="flex items-start space-x-3 mt-3">
                          <input
                            type="checkbox"
                            checked={formData.marketingConsent}
                            onChange={(e) => handleInputChange('marketingConsent', e.target.checked)}
                            className="mt-1 h-4 w-4 text-cyan-600 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            Acepto y autorizo el envío de información sobre el software y novedades de la plataforma (opcional)
                          </span>
                        </label>
                        <label className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            checked={formData.termsAccepted}
                            onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                            className="mt-1 h-4 w-4 text-cyan-600 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            Acepto los {' '}
                            <Dialog>
                              <DialogTrigger asChild>
                                <button type="button" className="text-cyan-600 underline">Términos de uso de información</button>
                              </DialogTrigger>
                              <DialogContent className="max-w-3xl">
                                <DialogTitle>Términos y Condiciones</DialogTitle>
                                <div className="mt-4">
                                  <TermsModalContent />
                                </div>
                              </DialogContent>
                            </Dialog>
                            {' '}*
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Anterior
                </Button>

                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={(e) => nextStep(e)}
                    className="bg-cyan-500 hover:bg-cyan-600"
                  >
                    Siguiente
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="bg-green-500 hover:bg-green-600"
                    disabled={submitting || !formData.termsAccepted}
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Registrando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Registrar Clínica
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          ¿Necesitas ayuda? <a href="mailto:soporte@tuclinicapp.com" className="text-cyan-600 hover:underline">Contáctanos</a>
        </p>
      </div>
    </div>
  );
};

export default RegisterClinic;
